import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DictionaryService } from '../services/dictionaryService';
import { Pronunciation, SenseGroup } from '../components/Entry';
import { usePageMeta, SITE_NAME } from '../lib/usePageMeta';

const formatDate = (date, options) => date.toLocaleDateString('en-US', options);

// The real words from the past six days, using the same daily rotation
const getEarlierDays = () =>
  [1, 2, 3, 4, 5, 6].map((daysAgo) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return { date, word: DictionaryService.getWordForDate(date) };
  });

const WordOfTheDayPage = () => {
  const [wordOfTheDay, setWordOfTheDay] = useState(null);
  const [earlierDays] = useState(getEarlierDays);

  usePageMeta(
    `Word of the Day | ${SITE_NAME}`,
    'A new English word every day, with its definition, pronunciation and an example of how to use it.'
  );

  useEffect(() => {
    DictionaryService.getWordOfTheDay().then(setWordOfTheDay);
  }, []);

  const entry = wordOfTheDay?.entry;

  return (
    <div className="wrap pt-12 md:pt-16">
      <h1 className="font-serif text-4xl font-semibold tracking-[-0.01em] md:text-5xl">Word of the day</h1>
      <p className="mt-2 text-lg text-ink-soft">{formatDate(new Date(), { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>

      {wordOfTheDay ? (
        <article className="mt-12">
          <h2 className="headword font-serif text-[clamp(3rem,9vw,6rem)] font-semibold leading-[1.05] tracking-[-0.02em]">
            {wordOfTheDay.word}
          </h2>
          {entry && <Pronunciation entry={entry} className="mt-5" />}

          {entry ? (
            <div className="mt-10">
              {entry.groups.slice(0, 2).map((group) => (
                <SenseGroup key={group.partOfSpeech} group={group} limit={3} headingLevel={3} />
              ))}
            </div>
          ) : (
            <p className="mt-6 text-lg text-ink-soft">The definition didn’t load here, but the full entry may.</p>
          )}

          <Link to={`/define/${wordOfTheDay.word}`} className="mt-4 inline-block text-lg text-link">
            Read the full definition of {wordOfTheDay.word}
          </Link>
        </article>
      ) : (
        <div className="mt-12 space-y-4 motion-safe:animate-pulse" aria-hidden="true">
          <div className="h-20 w-80 max-w-full rounded bg-ink/10" />
          <div className="h-5 w-full max-w-xl rounded bg-ink/10" />
        </div>
      )}

      <section className="mt-20 max-w-3xl">
        <h2 className="font-serif text-2xl font-semibold">Earlier this week</h2>
        <ul className="mt-6 divide-y divide-rule border-y border-rule">
          {earlierDays.map(({ date, word }) => (
            <li key={date.toDateString()}>
              <Link
                to={`/define/${word}`}
                className="-mx-2 grid grid-cols-[7.5rem_minmax(0,1fr)] items-baseline gap-4 rounded-sm px-2 py-4 transition-colors hover:bg-mark/30 sm:grid-cols-[10rem_minmax(0,1fr)]"
              >
                <span className="text-[0.95rem] text-ink-soft">{formatDate(date, { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                <span className="font-serif text-2xl">{word}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default WordOfTheDayPage;
